import type { InputIntent, SystemId } from "../core/types";

const SYSTEM_KEYS: Record<string, SystemId> = {
  Digit1: "propulsion",
  Digit2: "sonar",
  Digit3: "shield",
  Digit4: "repair",
  Digit5: "weapons",
};

export interface InputCallbacks {
  onPause: () => void;
  onRoutePower: (system: SystemId) => void;
}

export class InputController {
  private readonly pressed = new Set<string>();
  private touchX = 0;
  private touchZ = 0;
  private sonarQueued = false;
  private joystickPointer: number | null = null;

  constructor(
    private readonly joystick: HTMLElement,
    private readonly joystickKnob: HTMLElement,
    private readonly callbacks: InputCallbacks,
  ) {
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("keyup", this.handleKeyUp);
    window.addEventListener("blur", this.reset);
    joystick.addEventListener("pointerdown", this.handlePointerDown);
    joystick.addEventListener("pointermove", this.handlePointerMove);
    joystick.addEventListener("pointerup", this.handlePointerUp);
    joystick.addEventListener("pointercancel", this.handlePointerUp);
  }

  queueSonar(): void {
    this.sonarQueued = true;
  }

  read(): InputIntent {
    const keyboardX =
      (this.pressed.has("KeyD") || this.pressed.has("ArrowRight") ? 1 : 0) -
      (this.pressed.has("KeyA") || this.pressed.has("ArrowLeft") ? 1 : 0);
    const keyboardZ =
      (this.pressed.has("KeyS") || this.pressed.has("ArrowDown") ? 1 : 0) -
      (this.pressed.has("KeyW") || this.pressed.has("ArrowUp") ? 1 : 0);
    const magnitude = Math.hypot(keyboardX, keyboardZ);
    const sonar = this.sonarQueued;
    this.sonarQueued = false;

    if (magnitude > 0) {
      return {
        steerX: keyboardX / magnitude,
        steerZ: keyboardZ / magnitude,
        sonar,
      };
    }

    return { steerX: this.touchX, steerZ: this.touchZ, sonar };
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat && (event.code === "Space" || SYSTEM_KEYS[event.code])) return;

    if (event.code === "Space") {
      event.preventDefault();
      this.queueSonar();
      return;
    }

    if (event.code === "Escape" || event.code === "KeyP") {
      event.preventDefault();
      this.callbacks.onPause();
      return;
    }

    const system = SYSTEM_KEYS[event.code];
    if (system) {
      event.preventDefault();
      this.callbacks.onRoutePower(system);
      return;
    }

    this.pressed.add(event.code);
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    this.pressed.delete(event.code);
  };

  private handlePointerDown = (event: PointerEvent): void => {
    this.joystickPointer = event.pointerId;
    this.joystick.setPointerCapture(event.pointerId);
    this.updateJoystick(event);
  };

  private handlePointerMove = (event: PointerEvent): void => {
    if (event.pointerId === this.joystickPointer) this.updateJoystick(event);
  };

  private handlePointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.joystickPointer) return;
    this.joystickPointer = null;
    this.touchX = 0;
    this.touchZ = 0;
    this.joystickKnob.style.transform = "translate(0px, 0px)";
  };

  private updateJoystick(event: PointerEvent): void {
    const bounds = this.joystick.getBoundingClientRect();
    const radius = bounds.width * 0.32;
    const offsetX = event.clientX - (bounds.left + bounds.width / 2);
    const offsetY = event.clientY - (bounds.top + bounds.height / 2);
    const length = Math.max(1, Math.hypot(offsetX, offsetY));
    const scale = Math.min(1, radius / length);
    const x = offsetX * scale;
    const y = offsetY * scale;
    this.touchX = x / radius;
    this.touchZ = y / radius;
    this.joystickKnob.style.transform = `translate(${x}px, ${y}px)`;
  }

  private reset = (): void => {
    this.pressed.clear();
    this.touchX = 0;
    this.touchZ = 0;
    this.joystickKnob.style.transform = "translate(0px, 0px)";
  };
}
